import { Component } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { IntegrationsService } from './integrations.service'
import { OdooService } from './odoo/odoo.service'
import { ZohoService } from './zoho/zoho.service'
import { ShopifyService } from './shopify/shopify.service'
import { BuilderService } from '../builder-services/builder.service'

@Component({
  selector: 'app-integrations',
  templateUrl: './integrations.component.html',
  styleUrl: './integrations.component.scss'
})
export class IntegrationsComponent {
  integrationsForm!: FormGroup
  wordpressForm!: FormGroup
  shopifyForm!: FormGroup

  activePanelType: string | null = null

  // Visibility toggles
  showZohoClientId = false
  showZohoSecret = false
  showOdooApiKey = false
  showShopifySecret = false

  // Shopify OAuth
  shopifyAutoConnectUrl = ''

  // WordPress connection status
  wordpressConnected = false

  constructor(
    public zohoService: ZohoService,
    public odooService: OdooService,
    public shopifyService: ShopifyService,
    public builderService: BuilderService,
    private fb: FormBuilder,
    private integrationsService: IntegrationsService
  ) {}

  ngOnInit() {
    this.integrationsForm = this.fb.group({
      odooApiKey: [''],
      odooDataBaseName: [''],
      odooLogin: [''],
      odooUrl: [''],
      odooUserId: [{ value: '', disabled: true }],
      zohoAccessToken: [{ value: '', disabled: true }],
      zohoClientId: [''],
      zohoClientSecret: [''],
      zohoExpiresAt: [{ value: '', disabled: true }],
      zohoOrgId: [{ value: '', disabled: true }],
      zohoPlan: [{ value: '', disabled: true }],
      zohoRefreshToken: [{ value: '', disabled: true }]
    })

    this.wordpressForm = this.fb.group({
      wordpress_site_url: ['']
    })

    this.shopifyForm = this.fb.group({
      shopifyStoreUrl: [''],
      shopifyClientId: [''],
      shopifyClientSecret: ['']
    })

    // Load integrations from Firebase
    this.integrationsService.getIntegrations().subscribe((data: any) => {
      if (!data) return

      this.integrationsForm.patchValue({
        odooApiKey: data.odooApiKey || '',
        odooDataBaseName: data.odooDataBaseName || '',
        odooLogin: data.odooLogin || '',
        odooUrl: data.odooUrl || '',
        odooUserId: data.odooUserId || '',
        zohoAccessToken: data.zohoAccessToken || '',
        zohoClientId: data.zohoClientId || '',
        zohoClientSecret: data.zohoClientSecret || '',
        zohoExpiresAt: data.zohoExpiresAt || '',
        zohoOrgId: data.zohoOrgId || '',
        zohoPlan: data.zohoPlan || '',
        zohoRefreshToken: data.zohoRefreshToken || ''
      })

      // Load Shopify data
      this.shopifyForm.patchValue({
        shopifyStoreUrl: data.shopifyStoreUrl || '',
        shopifyClientId: data.shopifyClientId || '',
        shopifyClientSecret: data.shopifyClientSecret || ''
      })

      this.odooService.odooConnected = data?.odooConnected === true
      this.zohoService.zohoConnected = data?.zohoConnected === true
      this.shopifyService.shopifyConnected = data?.shopifyConnected === true

      if (data?.wordpress_site_url) {
        this.wordpressForm.patchValue({ wordpress_site_url: data.wordpress_site_url })
        this.wordpressConnected = true
      }
    })
  }

  // Panel Management
  openPanel(type: string) {
    this.activePanelType = type
  }

  closePanel() {
    this.activePanelType = null
  }

  // Save integrations to Firebase
  saveIntegrations() {
    const body = {
      ...this.integrationsForm.getRawValue(),
      odooConnected: this.odooService.odooConnected,
      zohoConnected: this.zohoService.zohoConnected,
      shopifyConnected: this.shopifyService.shopifyConnected,
      ...this.shopifyForm.value
    }

    this.integrationsService.setIntegrations(body).subscribe(() => {
      this.closePanel()
    })
  }

  // --- ZOHO ---
  connectToZoho() {
    this.saveIntegrations()
    this.zohoService.getAuthUrl(this.integrationsService.integrationsData).subscribe({
      next: (res: any) => {
        const url = res.data?.auth_url
        window.open(url, '_blank')
      }
    })
  }

  disconnectZoho() {
    this.zohoService.zohoConnected = false
    this.saveIntegrations()
  }

  // --- ODOO ---
  connectToOdoo() {
    const payload = {
      odooApiKey: this.integrationsForm.value.odooApiKey,
      odooDataBaseName: this.integrationsForm.value.odooDataBaseName,
      odooLogin: this.integrationsForm.value.odooLogin,
      odooUrl: this.integrationsForm.value.odooUrl
    }

    this.odooService.connect(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.integrationsForm.patchValue({
            odooUserId: res.data.userId
          })
          this.odooService.odooConnected = true
          this.saveIntegrations()
        } else {
          alert(res.message || 'Connection failed')
        }
      },
      error: () => alert('Connection failed')
    })
  }

  disconnectOdoo() {
    this.odooService.odooConnected = false
    this.saveIntegrations()
  }

  // --- WORDPRESS ---
  saveWordpressSettings() {
    const wordpress_site_url = this.wordpressForm.value.wordpress_site_url

    this.integrationsService.setIntegrations({
      wordpress_site_url,
      wordpressConnected: !!wordpress_site_url
    }).subscribe(() => {
      this.wordpressConnected = !!wordpress_site_url
      this.closePanel()
    })
  }

  disconnectWordpress() {
    this.wordpressForm.patchValue({ wordpress_site_url: '' })
    this.wordpressConnected = false
    this.saveWordpressSettings()
  }

  // --- SHOPIFY ---
  connectToShopify() {
    const storeUrl = this.shopifyForm.value.shopifyStoreUrl
    const clientId = this.shopifyForm.value.shopifyClientId
    const clientSecret = this.shopifyForm.value.shopifyClientSecret

    if (!storeUrl || !clientId || !clientSecret) return
    this.initiateShopifyOAuth(storeUrl, clientId, clientSecret)
  }

  private redirectToShopifyOAuth(url: string) {
    window.location.href = url
  }

  private initiateShopifyOAuth(storeUrl: string, clientId?: string, clientSecret?: string) {
    if (window.self !== window.top) {
      window.parent.postMessage({
        type: 'shopify-connect-request',
        storeUrl,
        clientId: clientId || null,
        clientSecret: clientSecret || null,
        projectId: this.shopifyService.projectId,
      }, '*')
    } else {
      this.shopifyService.getAuthUrl(storeUrl, clientId, clientSecret).subscribe({
        next: (res: any) => {
          const url = res.data?.auth_url
          if (url) this.redirectToShopifyOAuth(url)
        },
        error: () => alert('Failed to connect to Shopify')
      })
    }
  }

  saveShopifySettings() {
    const body = {
      ...this.integrationsForm.getRawValue(),
      ...this.shopifyForm.value,
      odooConnected: this.odooService.odooConnected,
      zohoConnected: this.zohoService.zohoConnected,
      shopifyConnected: this.shopifyService.shopifyConnected
    }

    this.integrationsService.setIntegrations(body).subscribe(() => {
      this.closePanel()
    })
  }

  disconnectShopify() {
    this.shopifyService.shopifyConnected = false
    this.saveShopifySettings()
  }

  // --- UTILITY ---
  copyToken() {
    const token = this.builderService.getToken() || ''
    navigator.clipboard.writeText(token).then(() => {
      alert('Token copied to clipboard!')
    })
  }

  copyProjectId() {
    const projectId = this.builderService.selectedProject
    navigator.clipboard.writeText(projectId).then(() => {
      alert('Project ID copied to clipboard!')
    })
  }
}
